import React, { useState } from 'react';
import { Search, FileText, Phone, Download, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DownloadReports = () => {
  const [phone, setPhone] = useState('');
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reports/${phone}`);
      setReports(data);
      if (data.length === 0) {
        toast.info('No reports found for this phone number. Please contact us if you believe this is an error.');
      }
    } catch (err) {
      toast.error('Failed to fetch reports. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <section className="bg-gradient-to-r from-[#0A2540] to-[#0EA5E9] py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <FileText className="h-16 w-16 text-white mx-auto mb-4" />
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="download-reports-heading"
          >
            Download Reports
          </h1>
          <p className="text-xl text-white/90">
            Access your lab reports and diagnostic results online
          </p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h2
              className="text-2xl font-bold text-[#0A2540] mb-2 text-center"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Find Your Reports
            </h2>
            <p className="text-center text-[#475569] mb-6">
              Enter your registered phone number to access your reports
            </p>

            <form onSubmit={handleSearch} className="space-y-4">
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Enter your 10-digit phone number"
                    className="pl-10"
                    data-testid="report-phone-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#0A2540] text-white hover:bg-[#0A2540]/90 rounded-full py-6 text-lg font-semibold"
                data-testid="search-reports-button"
              >
                <Search className="mr-2 h-5 w-5" />
                {loading ? 'Searching...' : 'Search Reports'}
              </Button>
            </form>

            {reports !== null && (
              <div className="mt-8">
                {reports.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-[#94A3B8] mx-auto mb-3" />
                    <p className="text-[#475569] font-medium">No reports found</p>
                    <p className="text-sm text-[#94A3B8] mt-1">
                      If you recently had tests done, your reports may still be processing.
                    </p>
                    <p className="text-sm text-[#94A3B8]">
                      Contact us at <a href="tel:6380608090" className="text-[#0EA5E9] hover:underline">6380608090</a> for assistance.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#0A2540]">
                      Reports Found ({reports.length})
                    </h3>
                    {reports.map((report) => (
                      <div
                        key={report.id || report.report_name}
                        className="border border-[#E2E8F0] rounded-xl p-4 flex items-center justify-between hover:shadow-md transition-all"
                        data-testid={`report-item-${report.report_name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="flex items-center">
                          <div className="bg-[#0EA5E9]/10 rounded-full p-3 mr-4">
                            <FileText className="h-5 w-5 text-[#0EA5E9]" />
                          </div>
                          <div>
                            <p className="font-semibold text-[#0A2540]">{report.report_name}</p>
                            <div className="flex items-center text-sm text-[#475569] space-x-3">
                              <span className="flex items-center">
                                <User className="h-3 w-3 mr-1" />
                                {report.patient_name}
                              </span>
                              <span>{report.report_date}</span>
                            </div>
                          </div>
                        </div>
                        <a
                          href={report.report_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`download-report-${report.report_name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <Button
                            variant="outline"
                            className="border-[#0EA5E9] text-[#0EA5E9] hover:bg-[#0EA5E9]/5 rounded-full"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DownloadReports;
